import { inject, Injectable } from "@angular/core";

import { Capacitor } from "@capacitor/core";
import { InAppReview } from "@capacitor-community/in-app-review";

import { SessionStorage } from "@dffedb/util";

import { Rolle } from "../bruger";

interface ReviewState {
    lastAskedAtMs: number | null;
}

const REVIEW_STATE_KEY = "appReview.state";
const REVIEW_COOLDOWN_DAYS = 180;

@Injectable({ providedIn: "root" })
export class AppRatingService {
    private readonly sessionStorage = inject(SessionStorage);

    public async requestReviewFromUserAction(): Promise<boolean> {
        if (!Capacitor.isNativePlatform() || !this.isForbruger()) {
            return false;
        }

        try {
            await InAppReview.requestReview();
            return true;
        } catch (error) {
            console.error("[AppRatingService] Error requesting review from user action:", error);
            return false;
        }
    }

    public markPromptShownIfEligible(): boolean {
        if (!Capacitor.isNativePlatform() || !this.isForbruger()) {
            return false;
        }

        const state = this.getState();
        if (!this.canRequestReview(state)) {
            return false;
        }

        state.lastAskedAtMs = Date.now();
        this.saveState(state);
        return true;
    }

    private canRequestReview(state: ReviewState): boolean {
        if (state.lastAskedAtMs && Date.now() - state.lastAskedAtMs < this.daysToMs(REVIEW_COOLDOWN_DAYS)) {
            return false;
        }

        return true;
    }

    private isForbruger(): boolean {
        const raw = this.sessionStorage.getItem("user");
        if (!raw) {
            return false;
        }

        try {
            const bruger = JSON.parse(raw) as { rolle?: Rolle };
            return Rolle.erForbruger(bruger?.rolle);
        } catch {
            return false;
        }
    }

    private getState(): ReviewState {
        const raw = this.sessionStorage.getItem(REVIEW_STATE_KEY);
        if (!raw) {
            return {
                lastAskedAtMs: null
            };
        }

        try {
            const state = JSON.parse(raw) as Partial<ReviewState>;
            return {
                lastAskedAtMs: Number.isFinite(state.lastAskedAtMs) ? Number(state.lastAskedAtMs) : null
            };
        } catch {
            return {
                lastAskedAtMs: null
            };
        }
    }

    private saveState(state: ReviewState): void {
        this.sessionStorage.setItem(REVIEW_STATE_KEY, JSON.stringify(state));
    }

    private daysToMs(days: number): number {
        return days * 24 * 60 * 60 * 1000;
    }
}
